import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  FileText,
  Download,
  Eye,
  Search,
  Filter,
  Calendar,
  Building2,
  Receipt,
  Shield,
  FileSpreadsheet,
  FolderOpen,
  ArrowLeft,
  Briefcase,
  Clock,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_ICONS = {
  levy_statement: Receipt,
  bank_statement: Building2,
  insurance: Shield,
  annual_financials: FileSpreadsheet,
  agm_minutes: FileText,
  maintenance_report: FileText,
  budget: FileSpreadsheet,
  audit_report: Search,
  other: FolderOpen,
};

const CATEGORY_COLORS = {
  levy_statement: 'bg-emerald-100 text-emerald-700',
  bank_statement: 'bg-blue-100 text-blue-700',
  insurance: 'bg-purple-100 text-purple-700',
  annual_financials: 'bg-amber-100 text-amber-700',
  agm_minutes: 'bg-sky-100 text-sky-700',
  maintenance_report: 'bg-orange-100 text-orange-700',
  budget: 'bg-indigo-100 text-indigo-700',
  audit_report: 'bg-rose-100 text-rose-700',
  other: 'bg-slate-100 text-slate-700',
};

export default function AdminFinancials() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [managingAgent, setManagingAgent] = useState(null);

  const hoaId = user?.hoa_id || 'demo_hoa';
  const token = localStorage.getItem('session_token');

  useEffect(() => {
    fetchData();
  }, [hoaId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch categories
      const catRes = await fetch(`${API_URL}/api/financials/categories`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (catRes.ok) {
        const catData = await catRes.json();
        setCategories(catData.categories || []);
      }

      // Fetch documents
      const docsRes = await fetch(`${API_URL}/api/financials/list/${hoaId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (docsRes.ok) {
        const docsData = await docsRes.json();
        setDocuments(docsData);
      }

      // Fetch managing agent info
      const agentRes = await fetch(`${API_URL}/api/hoa/${hoaId}/managing-agent`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (agentRes.ok) {
        const agentData = await agentRes.json();
        setManagingAgent(agentData);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      // Demo data
      setCategories([
        { id: 'levy_statement', name: 'Levy Statement' },
        { id: 'bank_statement', name: 'Bank Statement' },
        { id: 'insurance', name: 'Insurance Certificate' },
        { id: 'annual_financials', name: 'Annual Financials' },
        { id: 'agm_minutes', name: 'AGM Minutes' },
      ]);
      setDocuments([
        { document_id: '1', title: 'March 2026 Levy Statement', category: 'levy_statement', period: 'March 2026', uploaded_by_name: 'PropManage SA', created_at: new Date().toISOString() },
        { document_id: '2', title: 'Q1 2026 Bank Statement', category: 'bank_statement', period: 'Q1 2026', uploaded_by_name: 'PropManage SA', created_at: new Date().toISOString() },
        { document_id: '3', title: '2025-2026 Annual Report', category: 'annual_financials', period: 'FY 2025-2026', uploaded_by_name: 'PropManage SA', created_at: new Date().toISOString() },
      ]);
      setManagingAgent({ company_name: 'PropManage SA' });
    } finally {
      setLoading(false);
    }
  };

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.title?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || doc.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat?.name || categoryId?.replace('_', ' ');
  };

  const CategoryIcon = ({ category }) => {
    const Icon = CATEGORY_ICONS[category] || FolderOpen;
    return <Icon className="w-5 h-5" />;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4 lg:p-8" data-testid="admin-financials">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button 
            variant="ghost" 
            className="mb-2 -ml-2 text-slate-600"
            onClick={() => navigate('/admin')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">Financial Documents</h1>
              <p className="text-slate-500 mt-1">Documents uploaded by your managing agent</p>
            </div>
            {managingAgent && (
              <div className="flex items-center gap-2 px-4 py-2 bg-indigo-50 border border-indigo-200 rounded-lg">
                <Briefcase className="w-4 h-4 text-indigo-600" />
                <span className="text-sm text-indigo-700">
                  Managed by <span className="font-semibold">{managingAgent.company_name}</span>
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  placeholder="Search documents..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-full sm:w-48">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filter by category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Documents Grid */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-40 bg-slate-200 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : filteredDocuments.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No documents available</h3>
              <p className="text-slate-500">
                {managingAgent 
                  ? 'Your managing agent has not uploaded any financial documents yet.'
                  : 'No managing agent is assigned to this building.'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredDocuments.map(doc => (
              <Card key={doc.document_id} className="hover:shadow-lg transition-shadow cursor-pointer group" data-testid={`doc-${doc.document_id}`}>
                <CardContent className="p-5">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${CATEGORY_COLORS[doc.category] || 'bg-slate-100'}`}>
                    <CategoryIcon category={doc.category} />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2 group-hover:text-indigo-600 transition-colors">
                    {doc.title}
                  </h3>
                  <div className="space-y-2">
                    <Badge variant="secondary" className="text-xs">
                      {getCategoryName(doc.category)}
                    </Badge>
                    {doc.period && (
                      <div className="flex items-center gap-1 text-sm text-slate-500">
                        <Calendar className="w-3 h-3" />
                        {doc.period}
                      </div>
                    )}
                    <div className="flex items-center gap-1 text-sm text-slate-400">
                      <Clock className="w-3 h-3" />
                      {formatDate(doc.created_at)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-4 pt-4 border-t">
                    <Button variant="outline" size="sm" className="flex-1">
                      <Eye className="w-4 h-4 mr-1" />
                      View
                    </Button>
                    <Button variant="outline" size="sm" className="flex-1">
                      <Download className="w-4 h-4 mr-1" />
                      Download
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
